import React, { lazy, Suspense, useEffect } from "react"
import { BrowserRouter as Router, Routes, Route } from "react-router-dom"

import AppLoader from "../components/AppLoader"
import InternalError from "../components/InternalError"
import LandingLayout from "./landing/LandingLayout"
import CmsLayout from "./cms/CmsLayout"
import "../components/Header/CmsHeader.css"
import { useDispatch } from "react-redux"
import { setAuthData } from "./landing/authSlice"
import { getItem, getDecryptedItem, generateUniqueId } from "../helpers"
import Toaster from "../components/Toaster"
import LogoutModal from "../components/LogoutModal"
import { PageNotFound } from "../components/ErrorPages/PageNotFound"
import CMSAssets from "../components/CMSAssets"

const Login = lazy(() => import("./landing/login/Login"))
const Verify = lazy(() => import("./landing/verify/Verify"))
const GenerateForgotPassword = lazy(() => import("./landing/generate-forgot-password"))
const ResetPassword = lazy(() => import("./landing/reset-password/ResetPassword"))
const Dashboard = lazy(() => import("./cms/dashboard/Dashboard"))
const ChargerManagement = lazy(() => import("./cms/charger-management/ChargerManagement"))
const CompanyManagement = lazy(() => import("./cms/company-management/CompanyManagement"))
const UserManagement = lazy(() => import("./cms/user-management/UserManagement"))
const Billing = lazy(() => import("./cms/billing/Billing"))
const Complaints = lazy(() => import("./cms/complaints/Complaints"))
const Coupon = lazy(() => import("./cms/coupon/Coupon"))
const Profile = lazy(() => import("./cms/ProfilePage/ProfilePage"))
const Reports = lazy(() => import("./cms/reports/Reports"))
const Trigger = lazy(() => import("./cms/auto-trigger/AutoTriggers"))
const Subscription = lazy(() => import("./cms/Subscription"))
const Vas = lazy(() => import("./cms/vas"));
const Setting = lazy(() => import("./cms/configuration"));

const Application = () => {
    const dispatch = useDispatch()
    useEffect(() => {
        if (!localStorage?.APP_VERSION) {
            localStorage.clear()
            localStorage.setItem("APP_VERSION", process.env.REACT_APP_VERSION)
        } else if (localStorage.APP_VERSION != process.env.REACT_APP_VERSION) {
            localStorage.clear()
            window.location.href = "/auth"
            localStorage.setItem("APP_VERSION", process.env.REACT_APP_VERSION)
        } else if (getItem("isUserLoggedIn")) {
            const payload = {
                isLoggedIn: true,
                userInfo: getDecryptedItem("userInfo"),
                userRoles: getDecryptedItem("userRoles"),
            }
            dispatch(setAuthData(payload))
        }
    }, [])

    return (
        <>
            <Toaster vertical="top" horizontal="right" autoHideDuration={3000} />
            <LogoutModal />
            <Router>
                <Suspense fallback={<AppLoader />}>
                    <Routes>
                        <Route
                            path="/auth/*"
                            element={
                                <LandingLayout>
                                    <Login />
                                </LandingLayout>
                            }
                        />

                        <Route
                            path="/auth/verify"
                            element={
                                <LandingLayout>
                                    <Verify />
                                </LandingLayout>
                            }
                        />

                        {/* <Route
                            path="/auth/verify_password"
                            element={
                                <LandingLayout>
                                    <VerifyPassword />
                                </LandingLayout>
                            }
                        /> */}

                        <Route
                            path="/auth/forgot-password"
                            element={
                                <LandingLayout>
                                    <GenerateForgotPassword />
                                </LandingLayout>
                            }
                        />

                        <Route
                            path="/auth/generate-password"
                            element={
                                <LandingLayout>
                                    <GenerateForgotPassword />
                                </LandingLayout>
                            }
                        />

                        <Route
                            path="/auth/set-password"
                            element={
                                <LandingLayout>
                                    <ResetPassword />
                                </LandingLayout>
                            }
                        />

                        <Route
                            path="/auth/reset-password"
                            element={
                                <LandingLayout>
                                    <ResetPassword />
                                </LandingLayout>
                            }
                        />

                        {/* CMS Routes */}

                        {["/", "/dashboard/:component"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Dashboard />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/charger-management/:component", "charger-management"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <ChargerManagement />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/company-management/:component", "/company-management"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <CompanyManagement />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/user-management/:component", "/user-management"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <UserManagement />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/billing/:component", "/billing"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Billing />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/complaints/:component", "/complaints"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Complaints />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/coupon/:component", "/coupon"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Coupon />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        {["/p", "/profile/:component", "/profile"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Profile />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}
                        {["/reports/:component", "/reports"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Reports />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}
                        {["/auto-trigger/:component", "/auto-trigger/:component/:component", "/auto-trigger"].map(
                            path => (
                                <Route
                                    path={path}
                                    element={
                                        <CmsLayout>
                                            <Trigger />
                                        </CmsLayout>
                                    }
                                    key={generateUniqueId()}
                                />
                            )
                        )}

                        <Route
                            path="/cms-assets"
                            element={
                                <CmsLayout>
                                    <CMSAssets />
                                </CmsLayout>
                            }
                        />
                        {["/subscription/:component", "/subscription/subscription-details"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Subscription />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}
                        {["/vas/:component", "/vas"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Vas />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}
                        {["/configuration/:component/:action/:id", "/configuration/:component", "/configuration"].map(path => (
                            <Route
                                path={path}
                                element={
                                    <CmsLayout>
                                        <Setting />
                                    </CmsLayout>
                                }
                                key={generateUniqueId()}
                            />
                        ))}

                        <Route path="/internal_error" element={<InternalError />} />
                        <Route path="*" element={<PageNotFound />} />
                    </Routes>
                </Suspense>
            </Router>
        </>
    )
}
export default Application
